import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { PROJECT_CARDS, IMAGES } from "@/data/mockData";

const CATEGORIES = ["All", "Wraps", "Sweaters", "Blankets", "Accessories", "Beginner"];

const EDITORIAL_ROWS = [
  {
    kicker: "Slow project · Winter",
    title: "The Desert Wrap",
    body:
      "A weekend meditation, worn all winter. Airy garter stitch on generous needles — the kind of project that rewards you a row at a time.",
    yarn: "Alpaca-blend yarn",
    image: IMAGES.scarf,
  },
  {
    kicker: "Everyday · Any season",
    title: "Everyday Pullover",
    body:
      "One good sweater, worn for years. We paired the pattern with a soft alpaca blend that keeps its shape without going stiff.",
    yarn: "DK-weight merino",
    image: IMAGES.sweater,
  },
  {
    kicker: "Big rows · Small hours",
    title: "Hearth Blanket",
    body:
      "The kind of project you finish in a season and keep for a lifetime. Bulky wool, chunky stitches, slow evenings.",
    yarn: "Bulky wool blend",
    image: IMAGES.blanket,
  },
];

export default function Projects() {
  return (
    <div data-testid="projects-page" className="bg-cream">
      {/* Hero */}
      <section className="bg-bone border-b border-rule py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-4">Projects · An index</p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl tracking-editorial leading-[0.9]">
                Projects worth
                <br />
                {" "}<span className="italic text-clay">the long hours.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-ink leading-relaxed">
                Browse ideas by project type and experience level. Project names
                and material pairings are illustrative until the current
                selection is confirmed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b border-rule bg-cream py-5 sticky top-16 md:top-20 z-20">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16 flex items-center gap-2 md:gap-4 overflow-x-auto no-scrollbar">
          {CATEGORIES.map((c, i) => (
            <button
              key={c}
              data-testid={`projects-cat-${c.toLowerCase()}`}
              className={`shrink-0 px-4 py-2 text-xs uppercase tracking-eyebrow font-medium transition-colors border ${
                i === 0
                  ? "border-espresso bg-espresso text-cream"
                  : "border-rule hover:bg-oat"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Editorial featured rows */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-16 space-y-20 md:space-y-28">
          {EDITORIAL_ROWS.map((r, i) => (
            <article
              key={r.title}
              data-testid={`project-editorial-${i}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-7">
                <div className="aspect-[4/3] overflow-hidden bg-oat">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <p className="eyebrow mb-3 text-clay">{r.kicker}</p>
                <h2 className="font-serif text-4xl md:text-5xl tracking-editorial leading-[0.95]">
                  {r.title}
                </h2>
                <p className="mt-5 text-ink text-base md:text-lg leading-relaxed max-w-lg">
                  {r.body}
                </p>
                <div className="mt-6 flex items-center gap-3 text-sm">
                  <span className="text-[10px] uppercase tracking-eyebrow text-ink">
                    Pairs with
                  </span>
                  <Link
                    to="/shop"
                    className="border-b border-espresso/40 hover:border-espresso hover:text-clay transition-colors"
                  >
                    {r.yarn}
                  </Link>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/shop"
                    data-testid={`project-editorial-cta-${i}`}
                    className="btn-primary"
                  >
                    Shop the yarn
                  </Link>
                  <button className="btn-tertiary">
                    View pattern notes
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Grid of more projects */}
      <section className="bg-bone border-y border-rule py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl md:text-4xl tracking-editorial leading-tight">
              More to make.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 gap-y-12">
            {[...PROJECT_CARDS, ...PROJECT_CARDS].slice(0, 8).map((p, i) => (
              <div key={i} data-testid={`project-grid-${i}`} className="group">
                <div className="card-hover-image aspect-[3/4] bg-oat">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-serif text-lg mt-4 leading-tight">{p.title}</h3>
                <p className="text-xs text-ink mt-1">{p.pair}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beginners anchor */}
      <section id="beginners" className="bg-sand py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <p className="eyebrow mb-4 flex items-center justify-center gap-2 text-clay">
            <Sparkles className="w-3 h-3" strokeWidth={1.5} /> New to needles
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-editorial leading-tight">
            Start with one small thing.
          </h2>
          <p className="mt-4 text-ink text-base leading-relaxed max-w-lg mx-auto">
            A washcloth, a hat, a headband — any small project that teaches you
            one honest skill. We'll help you pick.
          </p>
          <Link to="/shop" data-testid="projects-beginner-cta" className="btn-primary mt-8 inline-flex">
            Beginner-friendly yarn
          </Link>
        </div>
      </section>
    </div>
  );
}
